// ========================================================
// FAST HIT Website — Router Configuration (Phase 9 Code-Split)
// Code-split route bundles using React.lazy and Suspense
// ========================================================

import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { PublicLayout } from '../components/layout/PublicLayout';
import { AppLayout } from '../components/layout/AppLayout';
import { ProtectedRoute, GuestRoute } from './guards';
import { PlaceholderPage } from '../pages/PlaceholderPage';
import { LoadingScreen } from '../components/common/LoadingScreen';

// Core landing page bundled directly for instant LCP
import { LandingPage } from '../pages/LandingPage';

// Lazy-loaded route chunks
const LiveScoresPage = lazy(() =>
  import('../pages/LiveScoresPage').then((m) => ({ default: m.LiveScoresPage }))
);
const FeaturesPage = lazy(() =>
  import('../pages/FeaturesPage').then((m) => ({ default: m.FeaturesPage }))
);
const PricingPage = lazy(() =>
  import('../pages/PricingPage').then((m) => ({ default: m.PricingPage }))
);
const MatchDetailPage = lazy(() =>
  import('../pages/MatchDetailPage').then((m) => ({ default: m.MatchDetailPage }))
);
const PlayersListPage = lazy(() =>
  import('../pages/PlayersListPage').then((m) => ({ default: m.PlayersListPage }))
);
const PlayerDetailPage = lazy(() =>
  import('../pages/PlayerDetailPage').then((m) => ({ default: m.PlayerDetailPage }))
);
const TeamsListPage = lazy(() =>
  import('../pages/TeamsListPage').then((m) => ({ default: m.TeamsListPage }))
);
const TeamDetailPage = lazy(() =>
  import('../pages/TeamDetailPage').then((m) => ({ default: m.TeamDetailPage }))
);
const TournamentsListPage = lazy(() =>
  import('../pages/TournamentsListPage').then((m) => ({ default: m.TournamentsListPage }))
);
const TournamentDetailPage = lazy(() =>
  import('../pages/TournamentDetailPage').then((m) => ({ default: m.TournamentDetailPage }))
);
const CommunityPage = lazy(() =>
  import('../pages/CommunityPage').then((m) => ({ default: m.CommunityPage }))
);
const SearchPage = lazy(() =>
  import('../pages/SearchPage').then((m) => ({ default: m.SearchPage }))
);
const PrivacyPolicyPage = lazy(() =>
  import('../pages/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage }))
);
const TermsPage = lazy(() =>
  import('../pages/TermsPage').then((m) => ({ default: m.TermsPage }))
);
const CommunityGuidelinesPage = lazy(() =>
  import('../pages/CommunityGuidelinesPage').then((m) => ({ default: m.CommunityGuidelinesPage }))
);

// Auth Lazy Chunks
const LoginPage = lazy(() =>
  import('../pages/auth/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const RegisterPage = lazy(() =>
  import('../pages/auth/RegisterPage').then((m) => ({ default: m.RegisterPage }))
);
const ForgotPasswordPage = lazy(() =>
  import('../pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage }))
);
const ResetPasswordPage = lazy(() =>
  import('../pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage }))
);

// Authenticated App Lazy Chunks
const AppHubPage = lazy(() =>
  import('../pages/app/AppHubPage').then((m) => ({ default: m.AppHubPage }))
);
const AppProfilePage = lazy(() =>
  import('../pages/app/AppProfilePage').then((m) => ({ default: m.AppProfilePage }))
);
const AppMyCricketPage = lazy(() =>
  import('../pages/app/AppMyCricketPage').then((m) => ({ default: m.AppMyCricketPage }))
);
const AppNotificationsPage = lazy(() =>
  import('../pages/app/AppNotificationsPage').then((m) => ({ default: m.AppNotificationsPage }))
);
const AppSettingsPage = lazy(() =>
  import('../pages/app/AppSettingsPage').then((m) => ({ default: m.AppSettingsPage }))
);
const AppTeamsPage = lazy(() =>
  import('../pages/app/AppTeamsPage').then((m) => ({ default: m.AppTeamsPage }))
);
const AppMatchesPage = lazy(() =>
  import('../pages/app/AppMatchesPage').then((m) => ({ default: m.AppMatchesPage }))
);
const AppTournamentsPage = lazy(() =>
  import('../pages/app/AppTournamentsPage').then((m) => ({ default: m.AppTournamentsPage }))
);
const AppScoringConsolePage = lazy(() =>
  import('../pages/app/AppScoringConsolePage').then((m) => ({ default: m.AppScoringConsolePage }))
);

function withSuspense(component: React.ReactNode) {
  return <Suspense fallback={<LoadingScreen />}>{component}</Suspense>;
}

export const router = createBrowserRouter([
  // Public Pages (Header + Footer)
  {
    path: '/',
    element: <PublicLayout />,
    children: [
      {
        index: true,
        element: <LandingPage />,
      },
      {
        path: 'features',
        element: withSuspense(<FeaturesPage />),
      },
      {
        path: 'live',
        element: <Navigate to="/live-scores" replace />,
      },
      {
        path: 'scores',
        element: <Navigate to="/live-scores" replace />,
      },
      {
        path: 'live-scores',
        element: withSuspense(<LiveScoresPage />),
      },
      {
        path: 'matches/:matchId',
        element: withSuspense(<MatchDetailPage />),
      },
      {
        path: 'players',
        element: withSuspense(<PlayersListPage />),
      },
      {
        path: 'players/:playerId',
        element: withSuspense(<PlayerDetailPage />),
      },
      {
        path: 'teams',
        element: withSuspense(<TeamsListPage />),
      },
      {
        path: 'teams/:teamId',
        element: withSuspense(<TeamDetailPage />),
      },
      {
        path: 'tournaments',
        element: withSuspense(<TournamentsListPage />),
      },
      {
        path: 'tournaments/:tournamentId',
        element: withSuspense(<TournamentDetailPage />),
      },
      {
        path: 'community',
        element: withSuspense(<CommunityPage />),
      },
      {
        path: 'search',
        element: withSuspense(<SearchPage />),
      },
      {
        path: 'pricing',
        element: withSuspense(<PricingPage />),
      },
      {
        path: 'news',
        element: (
          <PlaceholderPage
            title="Cricket News & Announcements"
            description="Latest updates, feature announcements, and community highlights."
            phase={1}
          />
        ),
      },
      {
        path: 'download',
        element: (
          <PlaceholderPage
            title="Download FAST HIT Mobile App"
            description="Get the FAST HIT mobile app for Android and iOS."
            phase={2}
          />
        ),
      },
      {
        path: 'contact',
        element: (
          <PlaceholderPage
            title="Contact FAST HIT Support"
            description="Have questions, suggestions, or need help? Reach out to our cricket team."
            phase={1}
          />
        ),
      },
      {
        path: 'privacy',
        element: withSuspense(<PrivacyPolicyPage />),
      },
      {
        path: 'terms',
        element: withSuspense(<TermsPage />),
      },
      {
        path: 'community-guidelines',
        element: withSuspense(<CommunityGuidelinesPage />),
      },

      // Guest-Only Routes (Login / Register / Password Reset)
      {
        path: 'login',
        element: (
          <GuestRoute>
            {withSuspense(<LoginPage />)}
          </GuestRoute>
        ),
      },
      {
        path: 'register',
        element: (
          <GuestRoute>
            {withSuspense(<RegisterPage />)}
          </GuestRoute>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <GuestRoute>
            {withSuspense(<ForgotPasswordPage />)}
          </GuestRoute>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <GuestRoute>
            {withSuspense(<ResetPasswordPage />)}
          </GuestRoute>
        ),
      },

      // 404 Catch-All within Public Layout
      {
        path: '*',
        element: (
          <PlaceholderPage
            title="404 — Page Not Found"
            description="The page you are looking for does not exist or has been moved."
          />
        ),
      },
    ],
  },

  // Authenticated App Routes (Header + App Shell, Protected)
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: withSuspense(<AppHubPage />),
      },
      {
        path: 'profile',
        element: withSuspense(<AppProfilePage />),
      },
      {
        path: 'my-cricket',
        element: withSuspense(<AppMyCricketPage />),
      },
      {
        path: 'teams',
        element: withSuspense(<AppTeamsPage />),
      },
      {
        path: 'matches',
        element: withSuspense(<AppMatchesPage />),
      },
      {
        path: 'tournaments',
        element: withSuspense(<AppTournamentsPage />),
      },
      {
        path: 'notifications',
        element: withSuspense(<AppNotificationsPage />),
      },
      {
        path: 'settings',
        element: withSuspense(<AppSettingsPage />),
      },
      {
        path: 'scoring/:matchId',
        element: withSuspense(<AppScoringConsolePage />),
      },
    ],
  },
]);
